import React, { useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { usePortal } from '../context/PortalContext';
import { tableService } from '../services/tableService';
import {
  TableHandHistoryAction,
  TableHandHistoryParticipant,
  TableHandHistoryPayload,
  TableSummary,
} from '../types';
import { formatCurrency, formatDateTime, getGameTypeLabel } from '../utils/formatters';

const renderSuitGlyph = (card?: string): React.ReactNode => {
  const suit = (card || '').slice(-1).toUpperCase();

  switch (suit) {
    case 'S':
      return <>&spades;</>;
    case 'H':
      return <>&hearts;</>;
    case 'D':
      return <>&diams;</>;
    case 'C':
      return <>&clubs;</>;
    default:
      return '?';
  }
};

const getSuitClassName = (card?: string): string => {
  const suit = (card || '').slice(-1).toUpperCase();

  switch (suit) {
    case 'H':
    case 'D':
      return 'portal-playing-card__suit portal-playing-card__suit--red';
    case 'S':
    case 'C':
      return 'portal-playing-card__suit portal-playing-card__suit--dark';
    default:
      return 'portal-playing-card__suit portal-playing-card__suit--dark';
  }
};

const getCardRank = (card?: string): string => {
  const rank = (card || '').slice(0, -1).toUpperCase();
  return rank || '?';
};

const getActionToneClass = (action: TableHandHistoryAction['action']): string => {
  switch (action) {
    case 'BET':
    case 'RAISE':
    case 'ALL_IN':
      return 'portal-hand-action__pill portal-hand-action__pill--aggressive';
    case 'CALL':
      return 'portal-hand-action__pill portal-hand-action__pill--neutral';
    case 'CHECK':
      return 'portal-hand-action__pill portal-hand-action__pill--passive';
    case 'FOLD':
      return 'portal-hand-action__pill portal-hand-action__pill--fold';
    default:
      return 'portal-hand-action__pill portal-hand-action__pill--neutral';
  }
};

const getResultToneClass = (value: number): string => {
  if (value > 0) return 'portal-hand-result portal-hand-result--positive';
  if (value < 0) return 'portal-hand-result portal-hand-result--negative';
  return 'portal-hand-result portal-hand-result--neutral';
};

const renderPlayingCard = (card: string, size: 'sm' | 'md' = 'md') => {
  if (!card) {
    return (
      <div className={`portal-playing-card portal-playing-card--${size} portal-playing-card--empty`}>
        <span>-</span>
      </div>
    );
  }

  return (
    <div className={`portal-playing-card portal-playing-card--${size}`}>
      <span className="portal-playing-card__rank">{getCardRank(card)}</span>
      <span className={getSuitClassName(card)}>{renderSuitGlyph(card)}</span>
    </div>
  );
};

const renderCardRow = (cards: string[], size: 'sm' | 'md' = 'md') => {
  if (!cards.length) {
    return <span className="portal-muted">Sem cartas abertas</span>;
  }

  return <div className="portal-playing-card-row">{cards.map((card, index) => <React.Fragment key={`${card}-${index}`}>{renderPlayingCard(card, size)}</React.Fragment>)}</div>;
};

const participantSort = (left: TableHandHistoryParticipant, right: TableHandHistoryParticipant): number => {
  const leftSeat = left.seat_position ?? Number.MAX_SAFE_INTEGER;
  const rightSeat = right.seat_position ?? Number.MAX_SAFE_INTEGER;
  return leftSeat - rightSeat;
};

const HistoryPage: React.FC = () => {
  const { selectedClub } = usePortal();
  const [tables, setTables] = useState<TableSummary[]>([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [history, setHistory] = useState<TableHandHistoryPayload | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedClub?.id) {
      setTables([]);
      return;
    }

    setError('');
    tableService
      .getClubTables(selectedClub.id)
      .then((rows) => {
        setTables(rows);
        setSelectedTableId((current) => current || rows[0]?.id || '');
      })
      .catch((nextError: any) => setError(nextError.message || 'Erro ao carregar mesas'));
  }, [selectedClub?.id]);

  useEffect(() => {
    if (!selectedTableId) {
      setHistory(null);
      return;
    }

    setLoading(true);
    setError('');

    tableService
      .getHandHistory(selectedTableId, { limit: 10, offset: 0 })
      .then(setHistory)
      .catch((nextError: any) => setError(nextError.message || 'Erro ao carregar historico'))
      .finally(() => setLoading(false));
  }, [selectedTableId]);

  const selectedTable = useMemo(
    () => tables.find((table) => table.id === selectedTableId) || null,
    [tables, selectedTableId]
  );

  const historySummary = useMemo(() => {
    const hands = history?.hands || [];
    const totalPot = hands.reduce((sum, hand) => sum + hand.pot_amount, 0);
    const totalRake = hands.reduce((sum, hand) => sum + hand.rake_amount, 0);
    const biggestPot = hands.reduce((max, hand) => Math.max(max, hand.pot_amount), 0);
    const uniquePlayers = new Set(
      hands.flatMap((hand) => hand.participants.map((participant) => participant.user_id))
    ).size;

    return {
      totalHands: hands.length,
      totalPot,
      totalRake,
      biggestPot,
      uniquePlayers,
    };
  }, [history]);

  return (
    <div className="portal-page-grid portal-page-stack">
      <section className="portal-section-card portal-section-card--hero portal-section-card--dashboard-hero">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Historico por mesa</p>
            <h3>Maos encerradas com leitura real de poker</h3>
            <p className="portal-muted">
              Board, participantes, vencedores e linha de acoes da mesa selecionada.
            </p>
          </div>
          <label className="portal-club-picker">
            <span>Mesa</span>
            <select value={selectedTableId} onChange={(event) => setSelectedTableId(event.target.value)}>
              {tables.map((table) => (
                <option key={table.id} value={table.id}>
                  {table.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="portal-dashboard-hero-stats">
          <article className="portal-dashboard-hero-stats__item">
            <span>Jogo</span>
            <strong>{getGameTypeLabel(selectedTable?.game_type)}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Blinds</span>
            <strong>
              {formatCurrency(selectedTable?.smallBlind || 0)} / {formatCurrency(selectedTable?.bigBlind || 0)}
            </strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Jogadores</span>
            <strong>
              {selectedTable?.currentPlayers || 0}/{selectedTable?.maxPlayers || 0}
            </strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Mesa</span>
            <strong>{selectedTable?.name || history?.table_name || '-'}</strong>
          </article>
        </div>

        <div className="portal-inline-stats portal-inline-stats--dashboard" style={{ marginTop: 18 }}>
          <div>
            <dt>Jogo</dt>
            <dd>{getGameTypeLabel(selectedTable?.game_type)}</dd>
          </div>
          <div>
            <dt>Blinds</dt>
            <dd>
              {formatCurrency(selectedTable?.smallBlind || 0)} / {formatCurrency(selectedTable?.bigBlind || 0)}
            </dd>
          </div>
          <div>
            <dt>Jogadores</dt>
            <dd>
              {selectedTable?.currentPlayers || 0}/{selectedTable?.maxPlayers || 0}
            </dd>
          </div>
        </div>
      </section>

      <section className="portal-metrics-grid portal-dashboard-metrics-grid">
        <MetricCard
          label="Maos carregadas"
          value={String(historySummary.totalHands)}
          helper="Amostra atual da mesa selecionada"
          variant="dashboard"
          theme="rake"
          badge="Hands"
        />
        <MetricCard
          label="Volume em potes"
          value={formatCurrency(historySummary.totalPot)}
          helper="Soma dos potes retornados"
          variant="dashboard"
          theme="deposit"
          badge="Volume"
        />
        <MetricCard
          label="Maior pote"
          value={formatCurrency(historySummary.biggestPot)}
          helper="Maior mao da amostra"
          variant="dashboard"
          theme="warning"
          badge="Pico"
        />
        <MetricCard
          label="Rake da amostra"
          value={formatCurrency(historySummary.totalRake)}
          helper="Rake total das maos listadas"
          variant="dashboard"
          theme="fee"
          badge="Rake"
        />
        <MetricCard
          label="Jogadores unicos"
          value={String(historySummary.uniquePlayers)}
          helper="Participantes distintos nas maos carregadas"
          variant="dashboard"
          theme="balance"
          badge="Players"
        />
        <MetricCard
          label="Mesa selecionada"
          value={selectedTable?.name || history?.table_name || '-'}
          helper="Escopo atual do historico"
          variant="dashboard"
          theme="receivable"
          badge="Contexto"
        />
      </section>

      <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-balance">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Hands</p>
            <h3>Leitura visual das ultimas maos</h3>
          </div>
          <span className="portal-chip">{history?.hands.length || 0} mao(s)</span>
        </div>

        {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}

        <div className="portal-history-hand-list">
          {loading ? (
            <p className="portal-muted">Carregando historico...</p>
          ) : history?.hands.length ? (
            history.hands.map((hand) => {
              const orderedParticipants = [...hand.participants].sort(participantSort);

              return (
                <article key={hand.id} className="portal-hand-card">
                  <div className="portal-hand-card__header">
                    <div>
                      <p className="portal-section-card__eyebrow">Mao {hand.id.slice(0, 8)}</p>
                      <h4>{selectedTable?.name || history.table_name}</h4>
                    </div>
                    <div className="portal-hand-card__meta">
                      <span className="portal-chip">Pote {formatCurrency(hand.pot_amount)}</span>
                      <span className="portal-chip">Rake {formatCurrency(hand.rake_amount)}</span>
                      <span className="portal-muted">{formatDateTime(hand.played_at)}</span>
                    </div>
                  </div>

                  <div className="portal-hand-card__section">
                    <div className="portal-hand-card__section-header">
                      <strong>Board final</strong>
                      <span className="portal-muted">{hand.community_cards.length} carta(s)</span>
                    </div>
                    {renderCardRow(hand.community_cards, 'md')}
                  </div>

                  <div className="portal-hand-card__section">
                    <div className="portal-hand-card__section-header">
                      <strong>Vencedores</strong>
                      <span className="portal-muted">
                        {hand.winners.length ? `${hand.winners.length} jogador(es)` : 'Sem vencedor registrado'}
                      </span>
                    </div>
                    {hand.winners.length ? (
                      <div className="portal-hand-winners">
                        {hand.winners.map((winner) => (
                          <div key={`${hand.id}-${winner.user_id}`} className="portal-hand-winner">
                            <strong>{winner.user_name}</strong>
                            <span>{formatCurrency(winner.winnings_amount)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="portal-muted">Sem vencedor detalhado para esta mao.</p>
                    )}
                  </div>

                  <div className="portal-hand-card__section">
                    <div className="portal-hand-card__section-header">
                      <strong>Participantes</strong>
                      <span className="portal-muted">{orderedParticipants.length} jogador(es)</span>
                    </div>
                    <div className="portal-hand-participants">
                      {orderedParticipants.map((participant) => (
                        <article key={`${hand.id}-${participant.user_id}`} className="portal-hand-participant">
                          <div className="portal-hand-participant__header">
                            <div>
                              <strong>{participant.user_name}</strong>
                              <p className="portal-muted">
                                Seat {participant.seat_position ?? '-'}
                              </p>
                            </div>
                            <span className={getResultToneClass(participant.net_result)}>
                              {formatCurrency(participant.net_result)}
                            </span>
                          </div>

                          <div className="portal-hand-participant__cards">
                            {renderCardRow(participant.cards || [], 'sm')}
                          </div>

                          <dl className="portal-hand-participant__stats">
                            <div>
                              <dt>Contribuiu</dt>
                              <dd>{formatCurrency(participant.contribution_amount)}</dd>
                            </div>
                            <div>
                              <dt>Recebeu</dt>
                              <dd>{formatCurrency(participant.winnings_amount)}</dd>
                            </div>
                          </dl>
                        </article>
                      ))}
                    </div>
                  </div>

                  <div className="portal-hand-card__section">
                    <div className="portal-hand-card__section-header">
                      <strong>Linha de acoes</strong>
                      <span className="portal-muted">{hand.actions.length} acao(oes)</span>
                    </div>
                    {hand.actions.length ? (
                      <div className="portal-hand-actions">
                        {hand.actions.map((action, index) => (
                          <div key={`${hand.id}-${action.user_id}-${action.created_at}-${index}`} className="portal-hand-action">
                            <span className="portal-hand-action__round">{action.round.replace('_', ' ')}</span>
                            <strong>{action.user_name}</strong>
                            <span className={getActionToneClass(action.action)}>{action.action}</span>
                            <span className="portal-muted">
                              {action.amount ? formatCurrency(action.amount) : 'Sem valor'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="portal-muted">Sem acoes registradas para esta mao.</p>
                    )}
                  </div>
                </article>
              );
            })
          ) : (
            <p className="portal-muted">Nenhuma mao historica retornada para a mesa selecionada.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default HistoryPage;
